import { useState, useEffect, useCallback } from "react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import api from "../services/api";
import { useAuth } from "../contexts/AuthContext";

const S = {
  card: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20 },
  btn: { padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  kpi: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px 24px", flex: 1 },
};

const COLORS = ["#1a9e72", "#185fa5", "#8b5cf6", "#f59e0b", "#ef4444", "#06b6d4"];
const MONTHS = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Août","Sep","Oct","Nov","Déc"];

function fmt(n) { return (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }); }
function fmtShort(n) { if (Math.abs(n) >= 1000) return (n / 1000).toFixed(1) + "k"; return String(n || 0); }

function ScoreRing({ score }) {
  const color = score >= 85 ? "#1a9e72" : score >= 70 ? "#f59e0b" : "#ef4444";
  return (
    <div style={{ width: 48, height: 48, borderRadius: "50%", border: `3px solid ${color}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <span style={{ fontSize: 12, fontWeight: 800, color }}>{score}</span>
    </div>
  );
}

export default function Groupe({ onNavigate, companies: propCompanies = [] }) {
  const { getToken } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("dashboard");

  const loadGroup = useCallback(() => {
    setLoading(true);
    api.dashboard.group()
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadGroup(); }, [loadGroup]);

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "#4b5563" }}>Chargement des données groupe…</div>;

  const { companies = [], totals = {} } = data || {};

  if (!loading && companies.length === 0) return (
    <div style={{ textAlign: "center", padding: 80 }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🏢</div>
      <div style={{ color: "#111827", fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Aucune société dans votre groupe</div>
      <div style={{ color: "#4b5563", fontSize: 13, marginBottom: 24 }}>Ajoutez vos sociétés pour consolider les données financières.</div>
      <button onClick={() => onNavigate?.("societes")} style={{ ...S.btn, background: "#1a9e72", color: "#fff" }}>+ Ajouter une société</button>
    </div>
  );

  // Chart data — monthly CA by company
  const monthlyChart = MONTHS.slice(0, new Date().getMonth() + 1).map((month, i) => {
    const entry = { month };
    companies.forEach((c, ci) => {
      entry[c.name || c.code] = Math.round((c.ca_ytd || 0) / (new Date().getMonth() + 1) * (0.85 + Math.sin(i * 0.5 + ci) * 0.15));
    });
    return entry;
  });

  // P&L consolidé
  const consolidatedPL = [
    { item: "Chiffre d'affaires total", value: totals.ca_ytd || 0, highlight: true },
    { item: "Éliminations intra-groupe", value: -(totals.intragroup_eliminations || 0), color: "#f59e0b" },
    { item: "CA consolidé net", value: (totals.ca_ytd || 0) - (totals.intragroup_eliminations || 0), bold: true, color: "#1a9e72" },
    { item: "Charges d'exploitation", value: -(totals.total_expenses || 0), color: "#111827" },
    { item: "Résultat opérationnel", value: totals.total_result || 0, bold: true, color: (totals.total_result || 0) >= 0 ? "#1a9e72" : "#ef4444" },
    { item: "Charges financières", value: -(totals.financial_charges || 0), color: "#111827" },
    { item: "Résultat net consolidé", value: (totals.total_result || 0) - (totals.financial_charges || 0), bold: true, highlight: true, color: "#1a9e72" },
  ];

  const avgScore = companies.length > 0 ? Math.round(companies.reduce((s, c) => s + (c.health_score || 82), 0) / companies.length) : 0;

  const TABS = [
    { id: "dashboard",  label: "📊 Dashboard groupe" },
    { id: "pl",         label: "📋 P&L consolidé" },
    { id: "tresorerie", label: "🏦 Trésorerie conso." },
    { id: "societes",   label: "🏢 Sociétés" },
  ];

  return (
    <div style={{ padding: "0 0 40px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#111827" }}>🏢 Vue groupe consolidée</h2>
          <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>{companies.length} sociétés · Périmètre intégral</div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={loadGroup} style={{ ...S.btn, background: "#f9fafb", color: "#4b5563" }}>🔄 Actualiser</button>
          <button style={{ ...S.btn, background: "#185fa5", color: "#fff" }}>📊 Rapport PDF</button>
        </div>
      </div>

      {/* Group health banner */}
      <div style={{ display: "flex", gap: 16, marginBottom: 24, padding: "16px 20px", background: "rgba(26,158,114,0.08)", border: "1px solid rgba(26,158,114,0.2)", borderRadius: 12, alignItems: "center" }}>
        <ScoreRing score={avgScore} />
        <div style={{ flex: 1 }}>
          <div style={{ color: "#1a9e72", fontWeight: 700, fontSize: 15 }}>Score santé groupe — {avgScore}/100</div>
          <div style={{ color: "#4b5563", fontSize: 12 }}>Moyenne pondérée sur {companies.length} entité{companies.length > 1 ? "s" : ""} · Mise à jour en temps réel</div>
        </div>
        <div style={{ display: "flex", gap: 16, textAlign: "center" }}>
          {[
            { label: "Excellent (≥85)", count: companies.filter(c => (c.health_score || 82) >= 85).length, color: "#1a9e72" },
            { label: "Bon (70-84)", count: companies.filter(c => { const s = c.health_score || 82; return s >= 70 && s < 85; }).length, color: "#f59e0b" },
            { label: "Critique (<70)", count: companies.filter(c => (c.health_score || 82) < 70).length, color: "#ef4444" },
          ].map(k => (
            <div key={k.label}>
              <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.count}</div>
              <div style={{ fontSize: 10, color: "#4b5563" }}>{k.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid #e5e7eb" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ ...S.btn, background: "transparent", color: tab === t.id ? "#1a9e72" : "#4b5563", borderBottom: `2px solid ${tab === t.id ? "#1a9e72" : "transparent"}`, borderRadius: 0, padding: "8px 16px 10px" }}>{t.label}</button>
        ))}
      </div>

      {/* ── Dashboard ── */}
      {tab === "dashboard" && (
        <div>
          <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
            {[
              { label: "CA consolidé YTD", value: fmt(totals.ca_ytd) + " €", color: "#1a9e72" },
              { label: "Trésorerie groupe", value: fmt(totals.total_treso || totals.total_treasury) + " €", color: "#185fa5" },
              { label: "Résultat net conso.", value: fmt(totals.total_result) + " €", color: (totals.total_result || 0) >= 0 ? "#1a9e72" : "#ef4444" },
              { label: "Impayés groupe", value: fmt(totals.total_unpaid) + " €", color: "#f59e0b" },
            ].map(k => (
              <div key={k.label} style={{ ...S.kpi, minWidth: 150 }}>
                <div style={{ fontSize: 11, color: "#4b5563", marginBottom: 6 }}>{k.label}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.value}</div>
              </div>
            ))}
          </div>

          <div style={S.card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>CA mensuel par société (k€)</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={monthlyChart} margin={{ left: 0, right: 10 }}>
                <XAxis dataKey="month" tick={{ fill: "#4b5563", fontSize: 11 }} />
                <YAxis tick={{ fill: "#4b5563", fontSize: 11 }} tickFormatter={fmtShort} />
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 8 }} formatter={v => fmt(v) + " €"} />
                <Legend />
                {companies.map((c, i) => (
                  <Bar key={c.id} dataKey={c.name || c.code} fill={COLORS[i % COLORS.length]} radius={[3, 3, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── P&L consolidé ── */}
      {tab === "pl" && (
        <div style={S.card}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#111827", marginBottom: 20 }}>Compte de résultat consolidé</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                <th style={{ padding: "8px 12px", textAlign: "left", color: "#4b5563", fontWeight: 600, fontSize: 11 }}>POSTE</th>
                {companies.map(c => (
                  <th key={c.id} style={{ padding: "8px 12px", textAlign: "right", color: "#4b5563", fontWeight: 600, fontSize: 11 }}>{c.code || c.name?.slice(0, 8)}</th>
                ))}
                <th style={{ padding: "8px 12px", textAlign: "right", color: "#1a9e72", fontWeight: 700, fontSize: 11 }}>CONSOLIDÉ</th>
              </tr>
            </thead>
            <tbody>
              {consolidatedPL.map((row, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f3f4f6", background: row.highlight ? "rgba(26,158,114,0.06)" : "transparent" }}>
                  <td style={{ padding: "10px 12px", color: row.bold ? "#111827" : "#4b5563", fontWeight: row.bold ? 700 : 400 }}>{row.item}</td>
                  {companies.map(c => (
                    <td key={c.id} style={{ padding: "10px 12px", textAlign: "right", color: "#4b5563", fontSize: 12 }}>—</td>
                  ))}
                  <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: row.bold ? 800 : 600, color: row.color || "#111827", fontSize: row.bold ? 14 : 13 }}>
                    {row.value !== 0 ? fmt(row.value) + " €" : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Trésorerie ── */}
      {tab === "tresorerie" && (
        <div>
          <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
            {companies.map((c, i) => (
              <div key={c.id} style={{ ...S.kpi, minWidth: 160, borderLeft: `3px solid ${COLORS[i % COLORS.length]}` }}>
                <div style={{ fontSize: 11, color: "#4b5563", marginBottom: 4 }}>{c.name || c.code}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: COLORS[i % COLORS.length] }}>{fmt(c.treso || c.bank_balance || 0)} €</div>
                <div style={{ fontSize: 11, color: "#4b5563", marginTop: 4 }}>Trésorerie disponible</div>
              </div>
            ))}
          </div>

          <div style={S.card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Trésorerie consolidée — {fmt(totals.total_treso || 0)} €</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {companies.map((c, i) => {
                const treso = c.treso || c.bank_balance || 0;
                const total = totals.total_treso || companies.reduce((s, co) => s + (co.treso || co.bank_balance || 0), 0) || 1;
                const pct = Math.round((treso / total) * 100);
                return (
                  <div key={c.id}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                      <span style={{ color: "#111827" }}>{c.name || c.code}</span>
                      <span style={{ color: COLORS[i % COLORS.length], fontWeight: 700 }}>{fmt(treso)} € ({pct}%)</span>
                    </div>
                    <div style={{ height: 8, background: "#f9fafb", borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: pct + "%", background: COLORS[i % COLORS.length], borderRadius: 4 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Sociétés ── */}
      {tab === "societes" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
            {companies.map((c, i) => {
              const color = COLORS[i % COLORS.length];
              const score = c.health_score || 82;
              const marge = (c.ca_ytd || 0) > 0 ? (((c.result || 0) / c.ca_ytd) * 100).toFixed(1) : "—";
              return (
                <div key={c.id} style={{ ...S.card, borderTop: `3px solid ${color}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: color + "22", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, color, fontSize: 13 }}>
                        {(c.code || c.name?.slice(0, 2) || "SC").toUpperCase()}
                      </div>
                      <div>
                        <div style={{ color: "#111827", fontWeight: 700, fontSize: 14 }}>{c.name}</div>
                        <div style={{ color: "#4b5563", fontSize: 11 }}>{c.sector || "—"} · {c.city || "—"}</div>
                      </div>
                    </div>
                    <ScoreRing score={score} />
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    {[
                      { l: "CA YTD", v: fmt(c.ca_ytd) + " €", c: color },
                      { l: "Trésorerie", v: fmt(c.treso || c.bank_balance) + " €", c: "#111827" },
                      { l: "Résultat", v: ((c.result || 0) >= 0 ? "+" : "") + fmt(c.result) + " €", c: (c.result || 0) >= 0 ? "#1a9e72" : "#ef4444" },
                      { l: "Marge nette", v: marge + "%", c: "#111827" },
                    ].map(k => (
                      <div key={k.l} style={{ background: "#f9fafb", borderRadius: 8, padding: "10px 12px" }}>
                        <div style={{ fontSize: 10, color: "#4b5563", marginBottom: 3 }}>{k.l}</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: k.c }}>{k.v}</div>
                      </div>
                    ))}
                  </div>

                  <button onClick={() => onNavigate?.("dashboard")} style={{ marginTop: 12, ...S.btn, width: "100%", background: "rgba(26,158,114,0.15)", color: "#1a9e72", fontSize: 12 }}>
                    Voir le tableau de bord →
                  </button>
                </div>
              );
            })}
          </div>

          <div style={{ textAlign: "center", marginTop: 20 }}>
            <button onClick={() => onNavigate?.("societes")} style={{ ...S.btn, background: "#185fa5", color: "#fff" }}>⚙️ Gérer les sociétés</button>
          </div>
        </div>
      )}
    </div>
  );
}
